import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorDisplayName } from '../../lib/doctor-name';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import {
  useCancelOpdAppointment,
  useOpdAppointments,
  useOpdLookup,
  useOpdSchedules,
  useRescheduleOpdAppointment,
} from './opd.queries';
import { LookupSelect } from './opd.shared';
import { formatOpdScheduleDate, formatOpdScheduleTime, initialOpdAppointmentFilters, todayInput } from './opd.utils';
import type { OpdAppointment, OpdListFilters } from './types';

const rowsPerPageOptions = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
];

type AppointmentConfirm = { type: 'register' | 'cancel'; row: OpdAppointment };

const upper = (value: string | null | undefined, fallback = '--') => (value || fallback).toUpperCase();

const initialTodayAppointmentFilters = (): OpdListFilters => {
  const date = todayInput();
  return { ...initialOpdAppointmentFilters(), fromDate: date, toDate: date };
};

const formatAppointmentDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hour12: true, timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')}, ${get('hour')}:${get('minute')} ${get('dayPeriod')}`.toUpperCase();
};

export const OpdAppointmentsPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [draft, setDraft] = useState<OpdListFilters>(initialTodayAppointmentFilters);
  const [filters, setFilters] = useState<OpdListFilters>(initialTodayAppointmentFilters);
  const [rescheduling, setRescheduling] = useState<OpdAppointment | null>(null);
  const [confirming, setConfirming] = useState<AppointmentConfirm | null>(null);
  const doctors = useOpdLookup('doctors');
  const query = useOpdAppointments(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });
  const schedules = useOpdSchedules(rescheduling?.doctor.id ?? '', todayInput(), '', true);
  const reschedule = useRescheduleOpdAppointment();
  const cancel = useCancelOpdAppointment();
  const busy = query.isFetching || reschedule.isPending || cancel.isPending;

  useEffect(() => {
    const totalPages = query.data?.pagination.totalPages;
    if (totalPages && filters.page > totalPages) setFilters((current) => ({ ...current, page: totalPages }));
  }, [filters.page, query.data?.pagination.totalPages]);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1 });
  };

  const reset = () => {
    const next = initialTodayAppointmentFilters();
    setDraft(next);
    setFilters(next);
  };

  const closeConfirm = () => {
    if (!cancel.isPending) setConfirming(null);
  };

  const confirmAction = () => {
    if (!confirming) return;
    if (confirming.type === 'register') {
      const id = confirming.row.id;
      setConfirming(null);
      void navigate(`/opd/register/${id}`);
      return;
    }
    cancel.mutate(confirming.row.id, {
      onSuccess: () => {
        showToast('Appointment cancelled successfully.', 'success');
        setConfirming(null);
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'Appointment could not be cancelled.', 'error'),
    });
  };

  return (
    <div className="page-stack opd-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">OPD</p>
          <h1>Appointments List</h1>
          <p>Enquiries, scheduled slots, registration, and rescheduling.</p>
        </div>
        <div className="opd-heading-actions">
          <Link className="button button-primary" to="/opd/appointments/new">New Enquiry</Link>
          <Link className="button button-secondary" to="/opd/register">New OPD Register</Link>
        </div>
      </div>

      <section className="content-card opd-card">
        <form className="opd-filter-grid" onSubmit={apply}>
          <select
            value={draft.field ?? ''}
            onChange={(event) => setDraft((current) => ({ ...current, field: event.target.value as OpdListFilters['field'], value: '' }))}
          >
            <option value="">SELECT FIELD</option>
            <option value="patientName">PATIENT NAME</option>
            <option value="doctor">Doctor</option>
          </select>
          {draft.field === 'doctor' ? (
            <LookupSelect
              id="appointment-doctor-filter"
              value={draft.value ?? ''}
              options={doctors.data}
              placeholder="Select Doctor"
              onChange={(value) => setDraft((current) => ({ ...current, value }))}
            />
          ) : (
            <input
              value={draft.value ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))}
              placeholder="Field Value"
              disabled={!draft.field}
            />
          )}
          <input type="date" value={draft.fromDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
          <input type="date" value={draft.toDate ?? ''} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
          <div className="opd-filter-actions">
            <button className="button button-primary" disabled={busy}>Filter</button>
            <button
              className="button opd-today"
              type="button"
              onClick={() => {
                const date = todayInput();
                setDraft((current) => ({ ...current, fromDate: date, toDate: date }));
                setFilters((current) => ({ ...current, fromDate: date, toDate: date, page: 1 }));
              }}
            >
              Today
            </button>
            <button className="button button-secondary" type="button" onClick={reset}>Reset</button>
          </div>
        </form>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load appointments" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title="No appointments found" message="Change the filters or create a new enquiry." /> : null}

        {query.isSuccess && query.data.data.length > 0 ? (
          <>
            <div className="table-scroll">
              <table className="opd-table appointment-table">
                <thead>
                  <tr>
                    <th>SN</th>
                    <SortableTh control={tableSort} column="appointmentDate">App. Date &amp; time</SortableTh>
                    <SortableTh control={tableSort} column="patient">Patient</SortableTh>
                    <th>Patient Type</th>
                    <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                    <SortableTh control={tableSort} column="bookingBy">Booking By</SortableTh>
                    <SortableTh control={tableSort} column="status">Status</SortableTh>
                    <th>Re-Schedule</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((row, index) => (
                    <tr key={row.id} className={row.status === 'registered' ? 'registered' : row.status === 'cancelled' ? 'cancelled' : 'scheduled'}>
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td>
                        <strong>{formatAppointmentDateTime(row.appointmentDate)}</strong>
                        <small>BOOKING: {formatAppointmentDateTime(row.bookingTime)}</small>
                      </td>
                      <td>
                        {row.patient ? (
                          <Link className="patient-name-link" to={`/patients/${row.patient.id}`} title="Open patient profile">
                            <strong>{upper(row.patientSnapshot.name)} ({upper(row.patientSnapshot.uhid)})</strong>
                          </Link>
                        ) : (
                          <strong>{upper(row.patientSnapshot.name)} ({upper(row.patientSnapshot.uhid)})</strong>
                        )}
                        <small>AGE: {row.patientSnapshot.ageYears || '--'} - MOBILE: {upper(row.patientSnapshot.phone)}</small>
                      </td>
                      <td><span className="opd-badge patient-type">{row.patient ? 'OLD' : 'NEW'} PATIENT</span></td>
                      <td>{doctorDisplayName(row.doctor.name)}</td>
                      <td>{row.bookingBy.name}</td>
                      <td>
                        <span className={`opd-badge ${row.status === 'registered' ? 'active' : row.status === 'cancelled' ? 'cancelled' : row.rescheduled ? 'rescheduled' : 'due'}`}>
                          {row.status === 'registered' ? 'Registered' : row.status === 'cancelled' ? 'Cancelled' : row.rescheduled ? 'Rescheduled' : 'Scheduled'}
                        </span>
                      </td>
                      <td>
                        {row.status === 'scheduled' ? (
                          <button className="opd-square-action" type="button" disabled={busy} onClick={() => setRescheduling(row)}>+</button>
                        ) : null}
                      </td>
                      <td>
                        {row.status === 'scheduled' ? (
                          <div className="opd-row-actions">
                            <button className="opd-register-action" type="button" disabled={busy} onClick={() => setConfirming({ type: 'register', row })}>Register</button>
                            <button type="button" disabled={busy} title="Cancel" aria-label="Cancel" onClick={() => setConfirming({ type: 'cancel', row })}>
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                                <path d="M5 5l14 14M19 5L5 19" />
                              </svg>
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination opd-pagination">
              <label>
                <span>Rows</span>
                <SearchableSelect
                  options={rowsPerPageOptions}
                  searchable={false}
                  value={String(filters.limit)}
                  disabled={busy}
                  onChange={(value) => {
                    const limit = Number(value);
                    setDraft((current) => ({ ...current, limit }));
                    setFilters((current) => ({ ...current, limit, page: 1 }));
                  }}
                />
              </label>
              <button className="button button-secondary" disabled={busy || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={busy || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>

      <Modal open={rescheduling !== null} closeOnBackdrop={false} className="opd-slot-modal">
        {rescheduling ? (
          <>
            <header>
              <div>
                <p>Appointment</p>
                <h2>Select Slot</h2>
              </div>
              <button type="button" onClick={() => setRescheduling(null)}>x</button>
            </header>
            <div className="opd-slot-modal-body">
              {schedules.isLoading ? <LoadingState /> : null}
              {schedules.data?.length === 0 ? <EmptyState title="No schedule found" message="Create active slots in Time Schedule Setup." /> : null}
              <div className="opd-doctor-schedule-grid">
                {schedules.data?.map((slot) => (
                  <button
                    type="button"
                    key={slot.id}
                    className={slot.available ? 'available' : 'full'}
                    disabled={!slot.available || reschedule.isPending}
                    onClick={() => void confirmUpdate('this appointment slot change').then((confirmed) => {
                      if (!confirmed || !rescheduling) return;
                      reschedule.mutate(
                        { id: rescheduling.id, slot: slot.id },
                        {
                          onSuccess: () => {
                            showToast('Appointment rescheduled successfully.', 'success');
                            setRescheduling(null);
                          },
                          onError: (error) => showToast(error instanceof ApiError ? error.message : 'Slot could not be changed.', 'error'),
                        },
                      );
                    })}
                  >
                    <strong>{formatOpdScheduleDate(slot.date)}</strong>
                    <span>{formatOpdScheduleTime(slot.date, slot.fromTime)} - {formatOpdScheduleTime(slot.date, slot.toTime)}</span>
                    <small>{slot.booked} / {slot.patientPerSlot}</small>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </Modal>

      <Modal open={confirming !== null} onClose={closeConfirm} alert labelledBy="appointment-confirm-title" describedBy="appointment-confirm-description" className="confirm-modal">
        {confirming ? (
          <>
            <span className={`confirm-modal-icon${confirming.type === 'cancel' ? ' danger' : ''}`} aria-hidden="true">{confirming.type === 'cancel' ? '!' : 'OK'}</span>
            <h2 id="appointment-confirm-title">{confirming.type === 'cancel' ? 'Cancel enquiry?' : 'Register appointment?'}</h2>
            <p id="appointment-confirm-description">
              {confirming.type === 'cancel'
                ? `Cancel enquiry for ${upper(confirming.row.patientSnapshot.name)} and free this appointment slot?`
                : `Open registration for ${upper(confirming.row.patientSnapshot.name)}?`}
            </p>
            <div className="bed-modal-actions confirm-modal-actions">
              <button className="button button-secondary" type="button" disabled={cancel.isPending} onClick={closeConfirm}>No</button>
              <button className="button button-primary" type="button" disabled={cancel.isPending} onClick={confirmAction}>
                {cancel.isPending && confirming.type === 'cancel' ? 'Cancelling...' : 'Yes'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
};
